import React, { useEffect, useRef } from "react";
import flatpickr from "flatpickr";

import "flatpickr/dist/flatpickr.css";
import "./FormDateInput.scss";




const FormDateInput = ({
    name,
    label,
    value = "",
    onChange,
    error = "",
    required = false,
    placeholder = "DD-MM-YYYY",
    minDate = null,
    maxDate = null,
    disabled = false,
    className = "",
}) => {
    const inputRef = useRef(null);
    const pickerRef = useRef(null);

    /*
    ==================================================
    FORMAT DATE
    ==================================================
    */
    const formatDate = (date) => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return "";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    };

    /*
    ==================================================
    PARSE DATE

    Supports:
    01-08-2026
    01/08/2026
    01.08.2026
    1-8-2026
    ==================================================
    */
    const parseDate = (value) => {
        if (!value) {
            return null;
        }

        const cleaned = String(value)
            .trim()
            .replace(/[/.]/g, "-");

        const match = cleaned.match(
            /^(\d{1,2})-(\d{1,2})-(\d{4})$/
        );

        if (!match) {
            return null;
        }

        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);

        const date = new Date(
            year,
            month - 1,
            day
        );

        /*
        Invalid dates:
        31-02-2026
        32-01-2026
        00-01-2026
        */
        if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
        ) {
            return null;
        }

        return date;
    };

    /*
    ==================================================
    NORMALIZE MIN/MAX DATE
    ==================================================
    */
    const normalizeDate = (date) => {
        if (!date) {
            return undefined;
        }

        if (date instanceof Date) {
            return date;
        }

        const parsed = parseDate(date);

        if (parsed) {
            return parsed;
        }

        return date;
    };

    /*
    ==================================================
    SEND VALUE TO PARENT
    ==================================================
    */
    const sendValue = (newValue) => {
        if (!onChange) {
            return;
        }

        onChange({
            target: {
                name,
                value: newValue,
            },
        });
    };

    /*
    ==================================================
    CHECK MIN/MAX
    ==================================================
    */
    const isAllowedDate = (date) => {
        if (!date) {
            return false;
        }

        const min = normalizeDate(minDate);
        const max = normalizeDate(maxDate);

        if (min instanceof Date && date < min) {
            return false;
        }

        if (max instanceof Date && date > max) {
            return false;
        }

        return true;
    };

    /*
    ==================================================
    COMMIT MANUAL INPUT

    This is called only when:
    - input loses focus and calendar is NOT open
    - calendar closes
    ==================================================
    */
    const commitInputValue = () => {
        if (!inputRef.current) {
            return;
        }

        const rawValue =
            inputRef.current.value.trim();

        /*
        Empty
        */
        if (!rawValue) {
            sendValue("");

            if (pickerRef.current) {
                pickerRef.current.clear(false);
            }

            return;
        }

        /*
        Parse
        */
        const date = parseDate(rawValue);

        /*
        Invalid date
        */
        if (!date) {
            /*
            Keep user's typed value.
            Validation can display the error.
            */
            sendValue(rawValue);
            return;
        }

        /*
        Min / max
        */
        if (!isAllowedDate(date)) {
            sendValue(rawValue);
            return;
        }

        /*
        Correct format
        */
        const formatted = formatDate(date);

        inputRef.current.value = formatted;

        /*
        Sync Flatpickr WITHOUT triggering onChange
        */
        if (pickerRef.current) {
            pickerRef.current.setDate(
                date,
                false
            );
        }

        sendValue(formatted);
    };

    /*
    ==================================================
    INITIALIZE FLATPICKR
    ==================================================
    */
    useEffect(() => {
        if (!inputRef.current) {
            return;
        }

        /*
        Destroy old instance
        */
        if (pickerRef.current) {
            pickerRef.current.destroy();
        }

        pickerRef.current = flatpickr(
            inputRef.current,
            {
                /*
                Display format
                */
                dateFormat: "d-m-Y",

                /*
                Allow normal typing
                */
                allowInput: true,

                /*
                Open when input/calendar clicked
                */
                clickOpens: true,

                /*
                Always use Flatpickr on mobile too
                */
                disableMobile: true,

                /*
                Date restrictions
                */
                minDate: normalizeDate(minDate),
                maxDate: normalizeDate(maxDate),

                /*
                Our DD-MM-YYYY parser
                */
                parseDate: (dateValue) => {
                    return parseDate(dateValue);
                },

                /*
                Our DD-MM-YYYY formatter
                */
                formatDate: (date) => {
                    return formatDate(date);
                },

                /*
                ==================================================
                CALENDAR DATE SELECTED
                ==================================================
                */
                onChange: (selectedDates) => {
                    if (!selectedDates.length) {
                        sendValue("");
                        return;
                    }

                    const selectedDate =
                        selectedDates[0];

                    const formatted =
                        formatDate(selectedDate);

                    if (inputRef.current) {
                        inputRef.current.value =
                            formatted;
                    }

                    sendValue(formatted);
                },

                /*
                ==================================================
                CALENDAR CLOSED
                ==================================================

                IMPORTANT:

                Previous month / next month / year / month
                buttons do NOT trigger onClose.

                Therefore they keep the calendar open.

                We only process the manually typed value
                after the calendar really closes.
                */
                onClose: () => {
                    commitInputValue();
                },
            }
        );

        /*
        Initial value
        */
        if (value) {
            const date = parseDate(value);

            if (date) {
                pickerRef.current.setDate(
                    date,
                    false
                );

                inputRef.current.value =
                    formatDate(date);
            } else {
                inputRef.current.value = value;
            }
        } else {
            inputRef.current.value = "";
        }

        /*
        Cleanup
        */
        return () => {
            if (pickerRef.current) {
                pickerRef.current.destroy();
                pickerRef.current = null;
            }
        };

        // Recreate only when date limits change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minDate, maxDate]);

    /*
    ==================================================
    SYNC VALUE FROM REACT
    ==================================================
    */
    useEffect(() => {
        if (!inputRef.current) {
            return;
        }

        /*
        VERY IMPORTANT:

        While calendar is open, DON'T call setDate().

        Otherwise:

        Previous Month
             ↓
        React value update
             ↓
        setDate()
             ↓
        Flatpickr jumps back / closes / resets
        */
        if (
            pickerRef.current?.isOpen
        ) {
            return;
        }

        const currentValue =
            inputRef.current.value || "";

        const nextValue = value || "";

        if (currentValue !== nextValue) {
            inputRef.current.value =
                nextValue;
        }

        if (!pickerRef.current) {
            return;
        }

        /*
        Empty value
        */
        if (!nextValue) {
            pickerRef.current.clear(false);
            return;
        }

        /*
        Valid React value
        */
        const date = parseDate(nextValue);

        if (date) {
            const selectedDate =
                pickerRef.current.selectedDates?.[0];

            if (
                !selectedDate ||
                selectedDate.getTime() !==
                date.getTime()
            ) {
                pickerRef.current.setDate(
                    date,
                    false
                );
            }
        }
    }, [value]);

    /*
    ==================================================
    MANUAL TYPING
    ==================================================
    */
    const handleInput = (event) => {
        /*
        DO NOT FORMAT WHILE TYPING.

        User can type:

        0
        01
        01-
        01-0
        01-08
        01-08-2
        01-08-2026
        */
        sendValue(event.target.value);
    };

    /*
    ==================================================
    INPUT BLUR
    ==================================================
    */
    const handleBlur = () => {
        /*
        Wait until browser/Flatpickr finishes
        handling the click.

        If user clicked:
        - previous month
        - next month
        - month
        - year

        Flatpickr is still open, so DON'T commit.
        */
        setTimeout(() => {
            if (
                pickerRef.current?.isOpen
            ) {
                return;
            }

            commitInputValue();
        }, 0);
    };

    /*
    ==================================================
    OPEN CALENDAR
    ==================================================
    */
    const openCalendar = () => {
        if (
            disabled ||
            !pickerRef.current
        ) {
            return;
        }

        pickerRef.current.open();
    };

    return (
        <div
            className={`form-date-input ${className}`}
        >
            {label && (
                <label className="form-label">
                    {label}

                    {required && (
                        <span className="required-star">
                            *
                        </span>
                    )}
                </label>
            )}

            <div className="date-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    name={name}
                    defaultValue={value || ""}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                    inputMode="numeric"
                    onInput={handleInput}
                    onBlur={handleBlur}
                    className={`date-input ${error
                        ? "is-invalid"
                        : ""
                        }`}
                />

                <button
                    type="button"
                    className="date-calendar-btn"
                    onMouseDown={(event) => {
                        /*
                        Prevent input blur when
                        clicking calendar icon.
                        */
                        event.preventDefault();
                    }}
                    onClick={openCalendar}
                    disabled={disabled}
                    tabIndex={-1}
                    aria-label="Open calendar"
                >
                    <i className="bi bi-calendar3" />
                </button>
            </div>

            {error && (
                <span className="date-error" style={{ borderColor: "#dc3545 !important" }}>
                    {error}
                </span>
            )}
        </div>
    );
};

export default FormDateInput;